import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { emailsAPI, emailTemplatesAPI, authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmailBodyEditor, { isEmailBodyEmpty } from './EmailBodyEditor';
import EmailLogAttachments from './EmailLogAttachments';
import { toast } from 'react-toastify';
import { canUserSendFromMailbox } from '../../utils/mailboxPermission';
import {
  FiSend, FiCornerUpLeft, FiMail, FiInbox, FiChevronRight, FiArrowLeft,
  FiEdit3, FiChevronDown, FiChevronUp, FiPaperclip, FiX, FiFile
} from 'react-icons/fi';

function getInitials(email) {
  if (!email) return '?';
  const name = email.split('@')[0].replace(/[._-]/g, ' ');
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function formatDate(dateStr, t) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('emailThread.justNow');
  if (mins < 60) return t('emailThread.mAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('emailThread.hAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('emailThread.dAgo', { count: days });
  return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const COLORS = ['#4F46E5', '#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626', '#0891B2', '#7C3AED', '#DB2777'];
function emailColor(email) {
  let hash = 0;
  for (let i = 0; i < (email || '').length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return COLORS[Math.abs(hash) % COLORS.length];
}

export default function EmailThreadPanel({
  entityType,
  entityId,
  entityEmail,
  defaultCc = '',
  allowSending = true,
  /** When true, compose/reply/send UI follows `allowSending` only (parent page enforces role + CRM rules). */
  useParentSendPermissionOnly = false
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPrivilegedEmailSender = ['super_admin', 'admin'].includes(String(user?.role || '').toLowerCase());
  const canUseSendUi = Boolean(allowSending) && (useParentSendPermissionOnly ? true : isPrivilegedEmailSender);
  const [mailboxes, setMailboxes] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replying, setReplying] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [openThreadId, setOpenThreadId] = useState(null);
  const [expandedThread, setExpandedThread] = useState(false);
  const [replyingToThread, setReplyingToThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [composeForm, setComposeForm] = useState({ mailbox_id: '', to: '', cc: '', subject: '', body_html: '' });
  const [composeFiles, setComposeFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedReplyTemplateId, setSelectedReplyTemplateId] = useState('');
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', subject_template: '', body_template: '' });
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const replyRef = useRef(null);
  const composeFileRef = useRef(null);
  const replyFileRef = useRef(null);
  const [sendWindowStatus, setSendWindowStatus] = useState(null);
  const [userSignature, setUserSignature] = useState('');
  const templateContextType = useMemo(() => {
    if (entityType === 'student') return 'student';
    if (entityType === 'lead') return 'lead';
    return 'mailbox';
  }, [entityType]);
  const templateContextId = useMemo(() => {
    if (entityType === 'student' || entityType === 'lead') return Number(entityId);
    return null;
  }, [entityType, entityId]);
  const lockComposeTo = useMemo(
    () => (entityType === 'student' || entityType === 'lead' || entityType === 'company') && !!String(entityEmail || '').trim(),
    [entityType, entityEmail]
  );

  const refreshSendWindow = useCallback(async () => {
    try {
      const res = await emailsAPI.getSendWindowStatus();
      setSendWindowStatus(res.data || null);
    } catch (_) {
      setSendWindowStatus(null);
    }
  }, []);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    const trimmedEntityEmail = String(entityEmail || '').trim();
    const params =
      entityType === 'student'
        ? { student_id: entityId, ...(trimmedEntityEmail ? { entity_email: trimmedEntityEmail } : {}) }
        : entityType === 'company'
          ? { company_id: entityId }
          : { lead_id: entityId, ...(trimmedEntityEmail ? { entity_email: trimmedEntityEmail } : {}) };

    // Always try to load threads first (read-only users may not have mailbox access)
    try {
      const threadsRes = await emailsAPI.getThreads(params);
      setEmailLogs(threadsRes.data || []);
    } catch (_) {
      setEmailLogs([]);
    }

    // Load mailboxes/templates only when sending UI is allowed
    if (canUseSendUi) {
      try {
        const [mailboxRes, templatesRes] = await Promise.all([
          emailsAPI.getMailboxes(),
          emailTemplatesAPI.list()
        ]);
        const boxes = mailboxRes.data || [];
        const templateRows = templatesRes.data || [];
        setMailboxes(boxes);
        setTemplates(templateRows);
        const sendable = boxes.filter(m => canUserSendFromMailbox(m, user));
        setComposeForm((prev) => ({
          ...prev,
          mailbox_id: prev.mailbox_id || sendable[0]?.mailbox_id || boxes[0]?.mailbox_id || '',
          to: lockComposeTo ? String(entityEmail || '').trim() : (prev.to || entityEmail || '')
        }));
        setSelectedTemplateId('');
      } catch (_) {
        setMailboxes([]);
        setTemplates([]);
      }
      refreshSendWindow();
    } else {
      setMailboxes([]);
      setTemplates([]);
      setSendWindowStatus(null);
    }

    setLoading(false);
  }, [entityType, entityId, entityEmail, user, refreshSendWindow, lockComposeTo, canUseSendUi]);

  useEffect(() => { loadEmails(); }, [loadEmails]);
  useEffect(() => {
    if (!lockComposeTo) return;
    setComposeForm((prev) => ({ ...prev, to: String(entityEmail || '').trim() }));
  }, [lockComposeTo, entityEmail]);
  useEffect(() => {
    const cc = String(defaultCc || '').trim();
    if (!cc) return;
    setComposeForm((prev) => (prev.cc ? prev : { ...prev, cc }));
  }, [defaultCc]);
  useEffect(() => {
    if (canUseSendUi) refreshSendWindow();
  }, [refreshSendWindow, canUseSendUi]);
  useEffect(() => {
    authAPI.getSignature()
      .then((res) => setUserSignature(res.data?.signature_html || ''))
      .catch(() => {});
  }, []);

  const sendableMailboxes = useMemo(() =>
    mailboxes.filter(m => canUserSendFromMailbox(m, user)),
    [mailboxes, user]
  );

  const threads = useMemo(() => {
    const map = new Map();
    emailLogs.forEach((log) => {
      const key = log.conversation_id || `solo_${log.email_log_id}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(log);
    });
    const result = [];
    map.forEach((messages, conversationId) => {
      messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const latest = messages[messages.length - 1];
      const first = messages[0];
      const subject = first.subject || t('emailThread.noSubject');
      const participants = [...new Set(messages.map(m => m.sender_email))];
      const hasUnread = messages.some(m => m.direction === 'RECEIVED' && !m.is_read);
      result.push({ conversationId, messages, latest, first, subject, participants, hasUnread, count: messages.length });
    });
    result.sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
    return result;
  }, [emailLogs, t]);

  const openThread = threads.find(t => t.conversationId === openThreadId);
  const replyPreview = useMemo(() => {
    if (!openThread) return { mailboxEmail: '', toEmail: '' };
    const lastReceived = [...openThread.messages].reverse().find(m => m.direction === 'RECEIVED') || openThread.latest;
    const replyMailboxId = lastReceived.mailbox_id || openThread.first.mailbox_id;
    const replyMailbox = mailboxes.find(m => m.mailbox_id === replyMailboxId);
    const mailboxEmail = String(replyMailbox?.mailbox_email || '');
    const senderEmail = String(lastReceived.sender_email || '');
    const recipientEmail = String(lastReceived.recipient_email || '');
    const computedTo =
      mailboxEmail && senderEmail && senderEmail.toLowerCase() === mailboxEmail.toLowerCase()
        ? recipientEmail
        : senderEmail;
    return { mailboxEmail, toEmail: computedTo || senderEmail || recipientEmail || '' };
  }, [openThread, mailboxes]);

  const canReplyToOpenThread = useMemo(() => {
    if (!openThread) return false;
    const lastReceived = [...openThread.messages].reverse().find(m => m.direction === 'RECEIVED') || openThread.latest;
    const mboxId = lastReceived.mailbox_id || openThread.first.mailbox_id;
    const mbox = mailboxes.find(m => m.mailbox_id === mboxId);
    if (mbox && !canUserSendFromMailbox(mbox, user)) return false;
    return true;
  }, [openThread, mailboxes, user]);

  const canSendByFrenchSchedule = !sendWindowStatus || sendWindowStatus.allowed;
  const showFrenchHoursBanner = sendWindowStatus && !sendWindowStatus.allowed && sendWindowStatus.applies_to_me;
  const canSendFromPanel = canUseSendUi;

  const buildFormData = (fields, files) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined) fd.append(k, v); });
    files.forEach(f => fd.append('attachments', f));
    return fd;
  };

  const applyTemplateToCompose = async (templateId) => {
    if (!templateId) return;
    try {
      const res = await emailTemplatesAPI.render(templateId, {
        lead_id: entityType === 'lead' ? Number(entityId) : null,
        student_id: entityType === 'student' ? Number(entityId) : null
      });
      setComposeForm((prev) => ({
        ...prev,
        subject: res.data?.subject || '',
        body_html: res.data?.body || ''
      }));
      await emailTemplatesAPI.markUsed(templateId, {
        context_type: templateContextType,
        context_id: templateContextId
      });
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailThread.failedApplyTemplate'));
    }
  };

  const applyTemplateToReply = async (templateId) => {
    if (!templateId) return;
    try {
      const res = await emailTemplatesAPI.render(templateId, {
        lead_id: entityType === 'lead' ? Number(entityId) : null,
        student_id: entityType === 'student' ? Number(entityId) : null
      });
      setReplyText(res.data?.body || '');
      await emailTemplatesAPI.markUsed(templateId, {
        context_type: templateContextType,
        context_id: templateContextId
      });
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailThread.failedApplyReply'));
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({ name: '', subject_template: '', body_template: '' });
    setEditingTemplateId(null);
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject_template || !templateForm.body_template) {
      toast.error(t('emailThread.templateRequired'));
      return;
    }
    try {
      if (editingTemplateId) {
        await emailTemplatesAPI.update(editingTemplateId, templateForm);
      } else {
        await emailTemplatesAPI.create(templateForm);
      }
      const res = await emailTemplatesAPI.list();
      setTemplates(res.data || []);
      resetTemplateForm();
      toast.success(editingTemplateId ? t('emailThread.templateUpdated') : t('emailThread.templateCreated'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailThread.templateSaveFailed'));
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm(t('emailThread.deleteTemplateConfirm'))) return;
    try {
      await emailTemplatesAPI.delete(templateId);
      const res = await emailTemplatesAPI.list();
      setTemplates(res.data || []);
      toast.success(t('emailThread.templateDeleted'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailThread.templateDeleteFailed'));
    }
  };

  const handleSend = async () => {
    if (!canSendFromPanel) {
      toast.error(t('emailThread.noReplyPermission'));
      return;
    }
    if (!canSendByFrenchSchedule) {
      toast.error(sendWindowStatus?.message || t('emailThread.sendWindowBlocked'));
      return;
    }
    if (!composeForm.mailbox_id || !composeForm.to || !composeForm.subject) {
      toast.error(t('emailThread.requiredFields')); return;
    }
    setSending(true);
    try {
      const fields = {
        mailbox_id: Number(composeForm.mailbox_id), to: composeForm.to,
        cc: composeForm.cc || '',
        subject: composeForm.subject, body_html: composeForm.body_html
      };
      if (entityType === 'student') fields.student_id = Number(entityId);
      else if (entityType === 'company') fields.company_id = Number(entityId);
      else fields.lead_id = Number(entityId);
      await emailsAPI.send(buildFormData(fields, composeFiles));
      if (selectedTemplateId) {
        await emailTemplatesAPI.markUsed(selectedTemplateId, {
          context_type: templateContextType,
          context_id: templateContextId
        });
      }
      toast.success(t('emailThread.emailSent'));
      setComposeForm(prev => ({ ...prev, cc: '', subject: '', body_html: '' }));
      setComposeFiles([]);
      setShowCompose(false);
      loadEmails();
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailThread.sendFailed'));
    }
    setSending(false);
  };

  const handleReply = async () => {
    if (!canSendFromPanel) {
      toast.error(t('emailThread.noReplyPermission'));
      return;
    }
    if (!canSendByFrenchSchedule) {
      toast.error(sendWindowStatus?.message || t('emailThread.sendWindowBlocked'));
      return;
    }
    if (isEmailBodyEmpty(replyText)) { toast.error(t('emailThread.replyRequired')); return; }
    const thread = openThread;
    if (!thread) return;
    const lastReceived = [...thread.messages].reverse().find(m => m.direction === 'RECEIVED') || thread.latest;
    const lastWithGraphId = [...thread.messages].reverse().find(m => m.graph_message_id);
    const replyMailboxId = lastReceived.mailbox_id || thread.first.mailbox_id;
    const replyMailbox = mailboxes.find(m => m.mailbox_id === replyMailboxId);
    if (replyMailbox && !canUserSendFromMailbox(replyMailbox, user)) {
      toast.error(t('emailThread.noReplyPermission'));
      return;
    }
    setReplying(true);
    try {
      const mailboxEmail = String(replyMailbox?.mailbox_email || '').toLowerCase();
      const senderEmail = String(lastReceived.sender_email || '').toLowerCase();
      const recipientEmail = String(lastReceived.recipient_email || '').toLowerCase();
      const computedTo =
        mailboxEmail && senderEmail === mailboxEmail
          ? (lastReceived.recipient_email || '')
          : (lastReceived.sender_email || '');

      const fields = {
        mailbox_id: Number(replyMailboxId),
        to: computedTo || (senderEmail && senderEmail !== mailboxEmail ? lastReceived.sender_email : lastReceived.recipient_email),
        subject: thread.subject, body_html: replyText,
        conversation_id: thread.conversationId.startsWith('solo_') ? '' : thread.conversationId,
        reply_to_graph_message_id: lastWithGraphId?.graph_message_id || ''
      };
      if (entityType === 'student') fields.student_id = Number(entityId);
      else if (entityType === 'company') fields.company_id = Number(entityId);
      else fields.lead_id = Number(entityId);
      await emailsAPI.reply(buildFormData(fields, replyFiles));
      if (selectedReplyTemplateId) {
        await emailTemplatesAPI.markUsed(selectedReplyTemplateId, {
          context_type: templateContextType,
          context_id: templateContextId
        });
      }
      toast.success(t('emailThread.replySent'));
      setReplyText(''); setReplyFiles([]); setReplyingToThread(null);
      loadEmails();
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailThread.replyFailed'));
    }
    setReplying(false);
  };

  const removeComposeFile = (idx) => setComposeFiles(prev => prev.filter((_, i) => i !== idx));
  const removeReplyFile = (idx) => setReplyFiles(prev => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    if (replyingToThread && replyRef.current) {
      replyRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      replyRef.current.querySelector('textarea')?.focus();
    }
  }, [replyingToThread]);

  useEffect(() => {
    const id = 'email-thread-panel-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = `
        .etp-thread-row:hover { background: var(--primary-light) !important; }
        .etp-compose-btn:hover { background: var(--primary-dark) !important; transform: translateY(-1px); }
        .etp-send-btn:hover { background: var(--primary-dark) !important; }
        .etp-reply-bar:hover { border-color: var(--primary) !important; background: var(--primary-light) !important; }
        .etp-back-btn:hover { opacity: 0.8; }
        .etp-compose-textarea:focus, .etp-reply-textarea:focus { border-color: var(--primary) !important; box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
        .etp-compose-input:focus { background: var(--primary-light) !important; }
        .etp-msg-wrapper + .etp-msg-wrapper { margin-top: 14px; }
        .etp-attach-btn:hover { background: var(--primary-light) !important; color: var(--primary) !important; }
        .etp-file-remove:hover { background: var(--danger-light) !important; color: var(--danger) !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // --- File attachment chip component ---
  const FileChips = ({ files, onRemove }) => {
    if (!files.length) return null;
    return (
      <div style={S.fileChipsWrap}>
        {files.map((f, i) => (
          <div key={i} style={S.fileChip}>
            <FiFile size={13} style={{ flexShrink: 0 }} />
            <span style={S.fileChipName}>{f.name}</span>
            <span style={S.fileChipSize}>{formatFileSize(f.size)}</span>
            <button className="etp-file-remove" style={S.fileRemove} onClick={() => onRemove(i)}><FiX size={12} /></button>
          </div>
        ))}
      </div>
    );
  };

  const AttachButton = ({ fileRef }) => (
    <button className="etp-attach-btn" style={S.attachBtn} onClick={() => fileRef.current?.click()}>
      <FiPaperclip size={14} /> {t('emailThread.attachFiles')}
    </button>
  );

  // --- LOADING ---
  if (loading) {
    return (
      <div style={S.loadingContainer}>
        <div style={S.spinner} />
        <span style={{ color: 'var(--text-muted)', marginTop: 12, fontSize: 14 }}>{t('emailThread.loadingEmails')}</span>
      </div>
    );
  }

  // --- THREAD DETAIL VIEW ---
  if (openThread) {
    const msgs = openThread.messages;
    const totalMsgs = msgs.length;
    const hasCollapsible = totalMsgs > 3;
    const collapsedMiddle = hasCollapsible && !expandedThread;
    const visibleMessages = collapsedMiddle ? [msgs[0], ...msgs.slice(-1)] : msgs;
    const hiddenCount = collapsedMiddle ? totalMsgs - 2 : 0;

    return (
      <div style={S.container} className="email-thread-panel">
        <div style={S.threadViewHeader}>
          <button className="etp-back-btn" onClick={() => { setOpenThreadId(null); setReplyingToThread(null); setReplyText(''); setExpandedThread(false); setReplyFiles([]); }} style={S.backBtn}>
            <FiArrowLeft size={18} /> {t('emailThread.backToInbox')}
          </button>
          <div style={S.threadSubjectLine}>
            <h3 style={S.threadSubject}>{openThread.subject}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={S.threadMsgCount}>{totalMsgs} {totalMsgs > 1 ? t('emailThread.messageCountPlural') : t('emailThread.messageCount')}</span>
              <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                {openThread.participants.map(p => p.split('@')[0]).join(', ')}
              </span>
            </div>
          </div>
        </div>

        {showFrenchHoursBanner && sendWindowStatus?.message && (
          <div style={{ padding: '10px 16px', margin: '0 0 8px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
            <strong>{t('emailThread.sendHours')}</strong>
            <div style={{ marginTop: 4 }}>{sendWindowStatus.message}</div>
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>{sendWindowStatus.schedule_label}</div>
          </div>
        )}

        <div style={S.messageList}>
          {visibleMessages.map((msg, visIdx) => {
            const realIdx = collapsedMiddle && visIdx === 1 ? totalMsgs - 1 : (collapsedMiddle ? 0 : msgs.indexOf(msg));
            const isFirst = realIdx === 0;
            const isReply = !isFirst;
            const isSent = msg.direction === 'SENT';
            const bgColor = emailColor(msg.sender_email);
            const prevMsg = realIdx > 0 ? msgs[realIdx - 1] : null;
            const senderName = msg.sender_email.split('@')[0];

            return (
              <React.Fragment key={msg.email_log_id}>
                {collapsedMiddle && visIdx === 1 && (
                  <div className="etp-thread-row" style={S.collapsedBar} onClick={() => setExpandedThread(true)}>
                    <div style={S.collapsedLine} />
                    <div style={S.collapsedChip}><FiChevronDown size={14} /><span>{hiddenCount} {hiddenCount > 1 ? t('emailThread.messageCountPlural') : t('emailThread.messageCount')}</span></div>
                    <div style={S.collapsedLine} />
                  </div>
                )}
                <div className="etp-msg-wrapper" style={{ position: 'relative' }}>
                  {isReply && !collapsedMiddle && <div style={S.threadConnector} />}
                  <div style={{ ...S.messageBubble, ...(isReply ? S.replyBubble : S.originalBubble) }}>
                    <div style={S.msgLabelBar}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ ...S.msgNumberBadge, background: isFirst ? 'var(--primary)' : 'var(--text-muted)' }}>#{realIdx + 1}</span>
                        {isFirst
                          ? <span style={S.originalLabel}>{t('emailThread.originalEmail')}</span>
                          : <span style={S.replyLabel}><FiCornerUpLeft size={12} />{prevMsg ? `${t('emailThread.replyLabel')} ${prevMsg.sender_email.split('@')[0]}` : t('emailThread.replyLabel')}</span>
                        }
                      </div>
                      <span style={S.msgLabelDate}>{formatDate(msg.created_at, t)}</span>
                    </div>
                    <div style={S.messageHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ ...S.avatar, backgroundColor: bgColor }}>{getInitials(msg.sender_email)}</div>
                        <div>
                          <div style={S.senderName}>
                            {senderName}
                            <span style={{ ...S.directionBadge, backgroundColor: isSent ? '#EEF2FF' : '#ECFDF5', color: isSent ? '#4F46E5' : '#059669' }}>
                              {isSent ? t('emailThread.sentBadge') : t('emailThread.receivedBadge')}
                            </span>
                          </div>
                          <div style={S.recipientLine}>
                            <span style={{ color: 'var(--text-light)' }}>{t('emailThread.fromLabel')}:</span> {msg.sender_email}
                            <span style={{ margin: '0 6px', color: 'var(--text-light)' }}>|</span>
                            <span style={{ color: 'var(--text-light)' }}>{t('emailThread.toLabel')}:</span> {msg.recipient_email}
                          </div>
                        </div>
                      </div>
                      <div style={S.messageDate}>{formatFullDate(msg.created_at)}</div>
                    </div>
                    <div style={S.messageBody} dangerouslySetInnerHTML={{ __html: msg.body_html || `<em style="color:#94a3b8">${t('emailThread.noContent')}</em>` }} />
                    <div style={{ padding: '0 16px 8px' }}>
                      <EmailLogAttachments
                        emailLogId={msg.email_log_id}
                        graphMessageId={msg.graph_message_id}
                        metadata={msg.metadata}
                        compact
                      />
                    </div>
                    {msg.mailbox_name && <div style={S.mailboxTag}>{t('emailThread.viaMailbox', { name: msg.mailbox_name })}</div>}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {!collapsedMiddle && totalMsgs > 3 && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button className="etp-thread-row" onClick={() => setExpandedThread(false)}
                style={{ ...S.collapsedChip, cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--white)' }}>
                <FiChevronUp size={14} /><span>{t('emailThread.collapse')}</span>
              </button>
            </div>
          )}
        </div>

        {!canSendFromPanel ? (
          <div style={{ padding: '10px 16px', background: 'var(--danger-light, #fff5f5)', borderRadius: 8, fontSize: 13, color: 'var(--danger, #e74c3c)', textAlign: 'center' }}>
            {t('emailThread.noReplyPermission')}
          </div>
        ) : !canReplyToOpenThread ? (
          <div style={{ padding: '10px 16px', background: 'var(--danger-light, #fff5f5)', borderRadius: 8, fontSize: 13, color: 'var(--danger, #e74c3c)', textAlign: 'center' }}>
            {t('emailThread.noReplyPermission')}
          </div>
        ) : !canSendByFrenchSchedule ? (
          <div style={{ padding: '10px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
            <strong>{t('emailThread.sendHours')}</strong>
            <div style={{ marginTop: 4 }}>{sendWindowStatus?.message}</div>
            <div style={{ marginTop: 4, fontSize: 12 }}>{sendWindowStatus?.schedule_label}</div>
          </div>
        ) : replyingToThread === openThreadId ? (
          <div ref={replyRef} style={S.replyContainer}>
            <div style={S.replyHeader}>
              <FiCornerUpLeft size={16} style={{ color: 'var(--primary)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{t('emailThread.replyLabel')} {openThread.latest.sender_email.split('@')[0]}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>{t('emailThread.fromLabel')}</div>
              <div style={{ fontSize: 13, color: 'var(--text)', wordBreak: 'break-word' }}>
                {replyPreview.mailboxEmail || '-'}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>{t('emailThread.toLabel')}</div>
              <div style={{ fontSize: 13, color: 'var(--text)', wordBreak: 'break-word' }}>
                {replyPreview.toEmail || '-'}
              </div>
            </div>
            <div style={S.replyQuoteBox}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>{openThread.latest.sender_email}{t('emailThread.wrote')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-light)', overflow: 'hidden', maxHeight: 60 }}>{stripHtml(openThread.latest.body_html).substring(0, 200)}</div>
            </div>
            <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                style={{ ...S.composeSelect, border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxWidth: 320 }}
                value={selectedReplyTemplateId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedReplyTemplateId(v);
                  if (v) applyTemplateToReply(v);
                }}
              >
                <option value="">{t('emailThread.replyTemplateOptional')}</option>
                {templates.map((tp) => (
                  <option key={tp.template_id} value={tp.template_id}>
                    {tp.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 4 }}>
              <EmailBodyEditor
                value={replyText}
                onChange={setReplyText}
                placeholder={t('emailThread.replyPlaceholder')}
                minHeight={160}
                compact
              />
            </div>
            {userSignature && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('common.signature')}</div>
                <div dangerouslySetInnerHTML={{ __html: userSignature }} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input ref={replyFileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => setReplyFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                <AttachButton fileRef={replyFileRef} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.cancelBtn} onClick={() => { setReplyingToThread(null); setReplyText(''); setReplyFiles([]); }}>{t('common.cancel')}</button>
                <button className="etp-send-btn" style={{ ...S.sendBtn, opacity: replying ? 0.7 : 1 }} onClick={handleReply} disabled={replying || !canSendByFrenchSchedule}>
                  <FiSend size={14} /> {replying ? t('emailThread.sendingBtn') : t('emailThread.sendReplyBtn')}
                </button>
              </div>
            </div>
            <FileChips files={replyFiles} onRemove={removeReplyFile} />
          </div>
        ) : (
          <div style={S.replyBarContainer}>
            <button className="etp-reply-bar" style={S.replyBar} onClick={() => setReplyingToThread(openThreadId)}>
              <FiCornerUpLeft size={16} /><span>{t('emailThread.replyPrompt')}</span>
            </button>
          </div>
        )}
        {showTemplateManager && (
          <div style={{ marginTop: 12, padding: 12, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 14 }}>{t('emailThread.emailTemplates')}</strong>
              <button type="button" style={S.cancelBtn} onClick={() => { setShowTemplateManager(false); resetTemplateForm(); }}>{t('common.close')}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input style={S.composeInput} placeholder={t('emailThread.templateNamePlaceholder')} value={templateForm.name} onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))} />
              <textarea style={{ ...S.composeTextarea, gridColumn: '1 / span 2', minHeight: 70 }} placeholder={t('emailThread.subjectPlaceholder')} value={templateForm.subject_template} onChange={(e) => setTemplateForm((p) => ({ ...p, subject_template: e.target.value }))} />
              <textarea style={{ ...S.composeTextarea, gridColumn: '1 / span 2', minHeight: 110 }} placeholder={t('emailThread.bodyPlaceholder')} value={templateForm.body_template} onChange={(e) => setTemplateForm((p) => ({ ...p, body_template: e.target.value }))} />
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button type="button" style={S.sendBtn} onClick={saveTemplate}>{editingTemplateId ? t('emailThread.updateTemplate') : t('emailThread.createTemplate')}</button>
              {editingTemplateId && <button type="button" style={S.cancelBtn} onClick={resetTemplateForm}>{t('emailThread.cancelEdit')}</button>}
            </div>
            <div style={{ marginTop: 12, maxHeight: 220, overflowY: 'auto' }}>
              {templates.map((tp) => (
                <div key={tp.template_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{tp.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tp.subject_template}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" style={S.cancelBtn} onClick={() => { setEditingTemplateId(tp.template_id); setTemplateForm({ name: tp.name, subject_template: tp.subject_template, body_template: tp.body_template }); }}>{t('common.edit')}</button>
                    <button type="button" style={S.cancelBtn} onClick={() => deleteTemplate(tp.template_id)}>{t('common.delete')}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- INBOX VIEW ---
    return (
    <div style={S.container} className="email-thread-panel">
      <div style={S.topBar}>
        <div style={S.topBarLeft}>
          <FiMail size={20} style={{ color: 'var(--primary)' }} />
          <span style={S.topBarTitle}>{t('emailThread.emailsTitle')}</span>
          {emailLogs.length > 0 && <span style={S.countChip}>{threads.length} {threads.length > 1 ? t('emailThread.threadCountPlural') : t('emailThread.threadCount')}</span>}
        </div>
        {canSendFromPanel && (
          <button
            className="etp-compose-btn"
            style={S.composeBtn}
            onClick={() => {
              if (!showCompose && !canSendByFrenchSchedule) {
                toast.error(sendWindowStatus?.message || t('emailThread.sendWindowCompose'));
                return;
              }
              setShowCompose(!showCompose);
              setSelectedTemplateId('');
            }}
          >
            <FiEdit3 size={15} /> {t('emailThread.composeBtn')}
          </button>
        )}
      </div>

      {showFrenchHoursBanner && sendWindowStatus?.message && (
        <div style={{ padding: '10px 16px', marginBottom: 8, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
          <strong>{t('emailThread.sendHours')}</strong>
          <div style={{ marginTop: 4 }}>{sendWindowStatus.message}</div>
          <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>{sendWindowStatus.schedule_label}</div>
        </div>
      )}

      {showCompose && canSendFromPanel && (
        <div style={S.composeCard}>
          <div style={S.composeHeader}><FiEdit3 size={16} style={{ color: 'var(--primary)' }} /><span style={{ fontWeight: 600, fontSize: 14 }}>{t('emailThread.newEmailTitle')}</span></div>
          <div>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                style={{ ...S.composeSelect, border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxWidth: 340 }}
                value={selectedTemplateId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedTemplateId(v);
                  if (v) applyTemplateToCompose(v);
                }}
              >
                <option value="">{t('emailThread.selectTemplate')}</option>
                {templates.map((tp) => (
                  <option key={tp.template_id} value={tp.template_id}>
                    {tp.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={S.composeRow}>
              <label style={S.composeLabel}>{t('emailThread.fromLabel')}</label>
              <select style={S.composeSelect} value={composeForm.mailbox_id} onChange={(e) => setComposeForm(f => ({ ...f, mailbox_id: e.target.value }))}>
                <option value="">{t('emailThread.selectMailbox')}</option>
                {sendableMailboxes.map(m => <option key={m.mailbox_id} value={m.mailbox_id}>{m.mailbox_name} ({m.mailbox_email})</option>)}
              </select>
            </div>
            <div style={S.composeRow}>
              <label style={S.composeLabel}>{t('emailThread.toLabel')}</label>
              <input
                className="etp-compose-input"
                style={{
                  ...S.composeInput,
                  ...(lockComposeTo ? { background: 'var(--bg)', cursor: 'not-allowed', opacity: 0.9 } : {})
                }}
                value={composeForm.to}
                onChange={(e) => {
                  if (lockComposeTo) return;
                  setComposeForm(f => ({ ...f, to: e.target.value }));
                }}
                placeholder={t('emailThread.recipientPlaceholder')}
                readOnly={lockComposeTo}
              />
            </div>
            <div style={S.composeRow}>
              <label style={S.composeLabel}>{t('emailThread.ccLabel')}</label>
              <input
                className="etp-compose-input"
                style={S.composeInput}
                value={composeForm.cc || ''}
                onChange={(e) => setComposeForm((f) => ({ ...f, cc: e.target.value }))}
                placeholder={t('emailThread.ccPlaceholder')}
              />
            </div>
            <div style={S.composeRow}>
              <label style={S.composeLabel}>{t('emailThread.subjectLabel')}</label>
              <input className="etp-compose-input" style={S.composeInput} value={composeForm.subject} onChange={(e) => setComposeForm(f => ({ ...f, subject: e.target.value }))} placeholder={t('emailThread.subjectInputPlaceholder')} />
            </div>
            <div style={{ padding: '0 16px 12px' }}>
              <EmailBodyEditor
                value={composeForm.body_html}
                onChange={(html) => setComposeForm((f) => ({ ...f, body_html: html }))}
                placeholder={t('emailThread.bodyInputPlaceholder')}
                minHeight={280}
              />
              {userSignature && (
                <div style={{ marginTop: 10, padding: '10px 14px', background: '#f9fafb', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 13 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>{t('emailThread.signaturePreview')}</div>
                  <div dangerouslySetInnerHTML={{ __html: userSignature }} />
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input ref={composeFileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => setComposeFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                <AttachButton fileRef={composeFileRef} />
                {userSignature && <span style={{ fontSize: 11, color: 'var(--text-light)' }}>{t('emailThread.signatureAutoAdd')}</span>}
              </div>
              <FileChips files={composeFiles} onRemove={removeComposeFile} />
            </div>
            <div style={S.composeFooter}>
              <button style={S.cancelBtn} onClick={() => { setShowCompose(false); setComposeFiles([]); }}>{t('common.cancel')}</button>
              <button className="etp-send-btn" style={{ ...S.sendBtn, opacity: sending ? 0.7 : 1 }} onClick={handleSend} disabled={sending || !canSendByFrenchSchedule}>
                <FiSend size={14} /> {sending ? t('emailThread.sendingBtn') : t('emailThread.sendEmailBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {threads.length === 0 ? (
        <div style={S.emptyState}>
          <FiInbox size={48} style={{ color: 'var(--text-light)' }} />
          <p style={{ color: 'var(--text-muted)', marginTop: 12, fontSize: 15 }}>{t('emailThread.noEmailsYet')}</p>
          <p style={{ color: 'var(--text-light)', fontSize: 13, marginTop: 4 }}>{t('emailThread.composeHint')}</p>
        </div>
      ) : (
        <div style={S.threadList}>
          {threads.map((thread) => {
            const preview = stripHtml(thread.latest.body_html).substring(0, 120);
            const latestSender = thread.latest.sender_email;
            const isSent = thread.latest.direction === 'SENT';
            return (
              <div key={thread.conversationId} className="etp-thread-row"
                style={{ ...S.threadRow, ...(thread.hasUnread ? S.threadRowUnread : {}) }}
                onClick={() => setOpenThreadId(thread.conversationId)}>
                <div style={{ ...S.avatar, backgroundColor: emailColor(latestSender), flexShrink: 0 }}>{getInitials(latestSender)}</div>
                <div style={S.threadContent}>
                  <div style={S.threadTop}>
                    <div style={S.threadParticipants}>
                      {thread.participants.length <= 2
                        ? thread.participants.map(p => p.split('@')[0]).join(', ')
                        : `${thread.participants[0].split('@')[0]} +${thread.participants.length - 1}`}
                      {thread.count > 1 && <span style={S.threadCountBadge}>{thread.count}</span>}
                    </div>
                    <div style={S.threadDate}>{formatDate(thread.latest.created_at, t)}</div>
                  </div>
                  <div style={S.threadSubjectPreview}>
                    <span style={{ fontWeight: thread.hasUnread ? 600 : 500, color: 'var(--text)' }}>{thread.subject}</span>
                  </div>
                  <div style={S.threadPreview}>
                    <span style={{ ...S.directionDot, backgroundColor: isSent ? '#4F46E5' : '#059669' }} />
                    <span style={{ color: 'var(--text-muted)' }}>{preview || t('emailThread.noContent')}</span>
                  </div>
                </div>
                <FiChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}
      {showTemplateManager && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 14 }}>{t('emailThread.emailTemplates')}</strong>
            <button type="button" style={S.cancelBtn} onClick={() => { setShowTemplateManager(false); resetTemplateForm(); }}>{t('common.close')}</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input style={S.composeInput} placeholder={t('emailThread.templateNamePlaceholder')} value={templateForm.name} onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))} />
            <textarea style={{ ...S.composeTextarea, gridColumn: '1 / span 2', minHeight: 70 }} placeholder={t('emailThread.subjectPlaceholder')} value={templateForm.subject_template} onChange={(e) => setTemplateForm((p) => ({ ...p, subject_template: e.target.value }))} />
            <textarea style={{ ...S.composeTextarea, gridColumn: '1 / span 2', minHeight: 110 }} placeholder={t('emailThread.bodyPlaceholder')} value={templateForm.body_template} onChange={(e) => setTemplateForm((p) => ({ ...p, body_template: e.target.value }))} />
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button type="button" style={S.sendBtn} onClick={saveTemplate}>{editingTemplateId ? t('emailThread.updateTemplate') : t('emailThread.createTemplate')}</button>
            {editingTemplateId && <button type="button" style={S.cancelBtn} onClick={resetTemplateForm}>{t('emailThread.cancelEdit')}</button>}
          </div>
          <div style={{ marginTop: 12, maxHeight: 220, overflowY: 'auto' }}>
            {templates.map((tp) => (
              <div key={tp.template_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{tp.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tp.subject_template}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" style={S.cancelBtn} onClick={() => { setEditingTemplateId(tp.template_id); setTemplateForm({ name: tp.name, subject_template: tp.subject_template, body_template: tp.body_template }); }}>{t('common.edit')}</button>
                  <button type="button" style={S.cancelBtn} onClick={() => deleteTemplate(tp.template_id)}>{t('common.delete')}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  container: { display: 'flex', flexDirection: 'column', gap: 0 },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: 'var(--white)', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0', borderBottom: '1px solid var(--border)' },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  topBarTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)' },
  countChip: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--primary-light)', color: 'var(--primary)' },
  composeBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'var(--transition)' },

  composeCard: { background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-md)', margin: '12px 0' },
  composeHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg)' },
  composeRow: { display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid var(--border-light)', padding: '0 16px' },
  composeLabel: { width: 92, fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', flexShrink: 0 },
  composeInput: { flex: 1, border: 'none', outline: 'none', padding: '12px 8px', fontSize: 14, fontFamily: 'var(--font)', background: 'transparent', color: 'var(--text)' },
  composeSelect: { flex: 1, border: 'none', outline: 'none', padding: '12px 8px', fontSize: 14, fontFamily: 'var(--font)', background: 'transparent', color: 'var(--text)', cursor: 'pointer' },
  composeTextarea: { width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius)', outline: 'none', padding: '12px', fontSize: 14, fontFamily: 'var(--font)', resize: 'vertical', minHeight: 120, color: 'var(--text)', transition: 'var(--transition)' },
  composeFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg)' },

  cancelBtn: { padding: '8px 16px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)' },
  sendBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'var(--transition)' },

  attachBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font)', transition: 'var(--transition)' },
  fileChipsWrap: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  fileChip: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--text-secondary)' },
  fileChipName: { maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 },
  fileChipSize: { fontSize: 10, color: 'var(--text-light)' },
  fileRemove: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, background: 'none', border: 'none', borderRadius: '50%', cursor: 'pointer', color: 'var(--text-muted)', transition: 'var(--transition)', padding: 0 },

  threadList: { background: 'var(--white)', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', border: '1px solid var(--border)', borderTop: 'none', overflow: 'hidden' },
  threadRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid var(--border-light)', cursor: 'pointer', transition: 'background 0.15s ease' },
  threadRowUnread: { background: '#F8FAFF' },
  threadContent: { flex: 1, minWidth: 0 },
  threadTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 },
  threadParticipants: { fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 },
  threadCountBadge: { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: 'var(--text-muted)', color: '#fff', lineHeight: '16px' },
  threadDate: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 },
  threadSubjectPreview: { fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 },
  threadPreview: { fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 },
  directionDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  avatar: { width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 0.5 },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', background: 'var(--white)', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', border: '1px solid var(--border)', borderTop: 'none' },

  threadViewHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', background: 'var(--white)', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0', borderBottom: '1px solid var(--border)' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', fontSize: 13, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', padding: '4px 0', flexShrink: 0, fontFamily: 'var(--font)' },
  threadSubjectLine: { flex: 1, minWidth: 0 },
  threadSubject: { fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 },
  threadMsgCount: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--primary-light)', color: 'var(--primary)', display: 'inline-block' },

  messageList: { background: 'var(--bg)', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 0, borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  messageBubble: { background: 'var(--white)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-xs)' },
  originalBubble: { borderLeft: '3px solid var(--primary)' },
  replyBubble: { borderLeft: '3px solid var(--success)', marginLeft: 24 },
  threadConnector: { position: 'absolute', left: 11, top: -12, width: 2, height: 12, background: 'var(--border)', borderRadius: 1 },

  msgLabelBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid var(--border-light)', background: 'var(--bg)' },
  msgNumberBadge: { width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 800 },
  originalLabel: { fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.5 },
  replyLabel: { fontSize: 11, fontWeight: 600, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4 },
  msgLabelDate: { fontSize: 11, color: 'var(--text-light)' },

  collapsedBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', margin: '4px 0', cursor: 'pointer' },
  collapsedLine: { flex: 1, height: 1, background: 'var(--border)' },
  collapsedChip: { display: 'flex', alignItems: 'center', gap: 4, padding: '4px 14px', borderRadius: 20, background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'var(--font)' },

  messageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 16px 8px', gap: 12, flexWrap: 'wrap' },
  senderName: { fontSize: 14, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 },
  directionBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  recipientLine: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  messageDate: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, paddingTop: 2 },
  messageBody: { padding: '4px 16px 14px', fontSize: 14, lineHeight: 1.7, color: 'var(--text-secondary)', wordBreak: 'break-word', overflow: 'hidden' },
  mailboxTag: { padding: '6px 16px', fontSize: 11, color: 'var(--text-light)', borderTop: '1px solid var(--border-light)', background: 'var(--bg)' },

  replyBarContainer: { padding: '12px 20px', background: 'var(--white)', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', border: '1px solid var(--border)', borderTop: 'none' },
  replyBar: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)', fontFamily: 'var(--font)', transition: 'var(--transition)' },
  replyContainer: { padding: '16px 20px', background: 'var(--white)', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', border: '1px solid var(--border)', borderTop: 'none' },
  replyHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  replyQuoteBox: { padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--radius)', borderLeft: '3px solid var(--border)', background: 'var(--bg)', overflow: 'hidden' },
  replyTextarea: { width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px', fontSize: 14, fontFamily: 'var(--font)', outline: 'none', resize: 'vertical', minHeight: 100, color: 'var(--text)', transition: 'var(--transition)' },
};
